import React, { useState } from "react";
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Menu } from "@mui/material";
import {
  resolveBeltVisual,
  shouldUseBeltView,
  shouldUseVectorBeltVisual,
  normalizeBeltToken,
} from "../../beltVisualCatalog";
import { isBeltColorPromotionAllowed } from "../../beltCatalog";
import { sportUsesStripeRank } from "../../beltSystemCatalog";
import BeltView, { BeltRankSleeve } from "./BeltView";
import { getMm } from "../../../theme/modernMaterialEnterprise";

const SOLID_WHITE_BELT_BORDER = "2px solid #000000";

function beltOutlineSx(belt) {
  const token = normalizeBeltToken(belt);
  if (token === "white") {
    return {
      border: SOLID_WHITE_BELT_BORDER,
      borderRadius: "4px",
      boxSizing: "border-box",
    };
  }
  return {
    border: "1px solid rgba(0,0,0,0.25)",
    boxSizing: "border-box",
  };
}

/**
 * Horizontal belt bar (SVG-style via CSS segments).
 */
export function BeltRankVisual({ belt, beltSystemKey = "bjj", width = 56, height = 14, sx = {} }) {
  const spec = resolveBeltVisual(belt, beltSystemKey);
  if (!spec) return null;

  return (
    <Box
      role="img"
      aria-label={belt}
      sx={{
        width,
        height,
        display: "flex",
        flexShrink: 0,
        borderRadius: "3px",
        overflow: "hidden",
        boxSizing: "border-box",
        border: spec.border || "1px solid rgba(0,0,0,0.25)",
        ...sx,
      }}
    >
      {spec.segments.map((seg, i) => (
        <Box
          key={i}
          sx={{
            flex: seg.flex ?? 1,
            bgcolor: seg.color,
            minWidth: 0,
          }}
        />
      ))}
    </Box>
  );
}

/**
 * Stripe count on black belt (0–4), vector style.
 */
export function StripeRankVisual({ stripeCount = 0, width = 40, height = 14, sx = {} }) {
  const n = Math.max(0, Math.min(4, Number(stripeCount) || 0));

  return (
    <Box
      role="img"
      aria-label={`${n} stripes`}
      sx={{
        width,
        height,
        flexShrink: 0,
        bgcolor: "#1A1A1A",
        borderRadius: "3px",
        border: "1px solid rgba(255,255,255,0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "2px",
        px: 0.5,
        boxSizing: "border-box",
        ...sx,
      }}
    >
      {n === 0 ? (
        <Typography component="span" sx={{ fontSize: "0.65rem", color: "#9CA3AF", lineHeight: 1 }}>
          0
        </Typography>
      ) : (
        Array.from({ length: n }).map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 3,
              height: "72%",
              bgcolor: "#FAC840",
              borderRadius: "1px",
            }}
          />
        ))
      )}
    </Box>
  );
}

/**
 * BJJ: Android-style BeltView vector. Other sports: segment vector bar.
 */
export function RankBeltDisplay({
  belt,
  beltSystemKey = "bjj",
  stripeCount = 0,
  width = 56,
  height = 14,
  forceVector = false,
  fullWidth = false,
  colorFocus = false,
  sx,
}) {
  if (!belt) return null;
  const key = (beltSystemKey || "bjj").toLowerCase();

  if (!forceVector && shouldUseBeltView(key)) {
    const stripes = sportUsesStripeRank(key) ? Number(stripeCount) || 0 : 0;
    return (
      <BeltView
        belt={belt}
        stripes={stripes}
        maxWidth={width}
        maxHeight={height}
        fullWidth={fullWidth}
        colorFocus={colorFocus}
        sx={sx}
      />
    );
  }

  return (
    <BeltRankVisual
      belt={belt}
      beltSystemKey={beltSystemKey}
      width={fullWidth ? "100%" : width}
      height={height}
      sx={{ ...beltOutlineSx(belt), ...(fullWidth ? { width: "100%" } : {}), ...sx }}
    />
  );
}

export function RankStripeDisplay({
  stripeCount = 0,
  belt = "black",
  beltSystemKey = "bjj",
  width = 40,
  height = 14,
  sx,
}) {
  const key = (beltSystemKey || "bjj").toLowerCase();
  if (!sportUsesStripeRank(key)) return null;

  const stripes = Number(stripeCount) || 0;
  if (stripes === 0) return null;

  if (shouldUseBeltView(key)) {
    return (
      <BeltRankSleeve
        belt={belt}
        stripes={stripes}
        maxWidth={width}
        maxHeight={height}
        sx={sx}
      />
    );
  }

  return (
    <StripeRankVisual stripeCount={stripes} width={width} height={height} sx={sx} />
  );
}

/** Shared compact Select field for Promotion by Group table cells */
export const promotionGroupSelectSx = (isDarkMode) => ({
  backgroundColor: getMm(isDarkMode).surfaceLowest,
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  "& .MuiInputBase-root": {
    height: 36,
  },
  "& .MuiSelect-select": {
    py: "0 !important",
    pl: "8px !important",
    pr: "28px !important",
    minHeight: "34px !important",
    height: "34px !important",
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
    color: getMm(isDarkMode).onSurface,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: getMm(isDarkMode).outlineVariant,
    borderRadius: "8px",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: getMm(isDarkMode).onSurface,
  },
  "&.Mui-disabled": {
    backgroundColor: isDarkMode ? "#374151" : "#9CA3AF",
    opacity: 0.6,
  },
});

/** Outlined selects in dialogs / forms (no fixed 34px height — avoids label overlap). */
export const promotionDialogSelectSx = (isDarkMode) => ({
  backgroundColor: getMm(isDarkMode).surfaceLowest,
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: getMm(isDarkMode).outlineVariant,
    borderRadius: "8px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: getMm(isDarkMode).outline,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: getMm(isDarkMode).onSurface,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    py: 1.25,
    px: 1.5,
    minHeight: "1.4375em",
    boxSizing: "border-box",
    color: getMm(isDarkMode).onSurface,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  "& .MuiSelect-icon": {
    color: getMm(isDarkMode).onSurface,
  },
});

function StackedFieldLabel({ children, isDarkMode }) {
  if (!children) return null;
  return (
    <Typography
      component="label"
      sx={{
        display: "block",
        mb: 0.75,
        fontSize: "0.8125rem",
        fontWeight: 600,
        color: getMm(isDarkMode).onSurface,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        lineHeight: 1.3,
      }}
    >
      {children}
    </Typography>
  );
}

/** Stripe / simple value select for promotion dialogs (stacked label, no notch overlap). */
export function PromotionDialogSelectField({
  label,
  value,
  onChange,
  disabled = false,
  isDarkMode,
  placeholder,
  displayEmpty = false,
  renderValue,
  children,
  fullWidth = true,
}) {
  return (
    <Box sx={{ width: fullWidth ? "100%" : undefined, mb: 0 }}>
      <StackedFieldLabel isDarkMode={isDarkMode}>{label}</StackedFieldLabel>
      <FormControl fullWidth={fullWidth} size="small" disabled={disabled}>
        <Select
          displayEmpty={displayEmpty}
          value={value}
          onChange={onChange}
          renderValue={renderValue}
          MenuProps={{
            disableScrollLock: true,
            PaperProps: {
              sx: {
                maxHeight: 320,
                backgroundColor: getMm(isDarkMode).surfaceLowest,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              },
            },
          }}
          sx={promotionDialogSelectSx(isDarkMode)}
        >
          {children}
        </Select>
      </FormControl>
    </Box>
  );
}

/** Selected promotion belt: card UI (no Select chrome). Click opens change menu. */
export function PromotionBeltSelectedCard({
  belt,
  beltSystemKey = "bjj",
  formatBeltName,
  isDarkMode,
  onClick,
  disabled,
  changeHint,
  visualVariant = "compact",
  colorFocus = false,
}) {
  if (!belt) return null;
  const label =
    typeof formatBeltName === "function"
      ? formatBeltName(belt)
      : belt;
  const isProminent = visualVariant === "prominent";

  return (
    <Box
      role={disabled ? undefined : "button"}
      tabIndex={disabled ? undefined : 0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={
        disabled
          ? undefined
          : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.(e);
              }
            }
      }
      title={disabled ? undefined : changeHint}
      sx={{
        display: "flex",
        flexDirection: isProminent ? "column" : "row",
        alignItems: isProminent ? "stretch" : "center",
        gap: isProminent ? 1 : 1,
        px: isProminent ? 1.25 : 1,
        py: isProminent ? 1.25 : 0.75,
        minHeight: isProminent ? 72 : 36,
        width: "100%",
        boxSizing: "border-box",
        borderRadius: "8px",
        border: "1px solid",
        borderColor: getMm(isDarkMode).outlineVariant,
        bgcolor: getMm(isDarkMode).surfaceLow,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.65 : 1,
        transition: "border-color 0.15s ease, background-color 0.15s ease",
        ...(!disabled && {
          "&:hover": {
            borderColor: isDarkMode ? "#6B7280" : "#D1D5DB",
            bgcolor: isDarkMode ? "#4B5563" : "#F3F4F6",
          },
        }),
      }}
    >
      <RankBeltDisplay
        belt={belt}
        beltSystemKey={beltSystemKey}
        width={isProminent ? 320 : 52}
        height={isProminent ? 24 : 12}
        fullWidth={isProminent}
        colorFocus={colorFocus && !isProminent}
        sx={isProminent ? { width: "100%" } : { flexShrink: 0 }}
      />
      <Typography
        component="span"
        noWrap={!isProminent}
        sx={{
          flex: isProminent ? undefined : 1,
          minWidth: 0,
          fontSize: "0.8125rem",
          fontWeight: 600,
          lineHeight: 1.2,
          color: getMm(isDarkMode).onSurface,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          textAlign: "left",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function renderBeltMenuItemContent(
  belt,
  beltSystemKey,
  formatBeltName,
  disabled,
  visualVariant = "compact",
  colorFocus = false
) {
  const label = typeof formatBeltName === "function" ? formatBeltName(belt) : belt;
  const isProminent = visualVariant === "prominent";

  if (isProminent) {
    return (
      <Box
        sx={{
          width: "100%",
          py: 0.25,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          opacity: disabled ? 0.45 : 1,
        }}
      >
        <RankBeltDisplay
          belt={belt}
          beltSystemKey={beltSystemKey}
          width={320}
          height={24}
          fullWidth
          sx={{ width: "100%", mb: 0.75 }}
        />
        <Typography
          component="span"
          sx={{
            display: "block",
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: "inherit",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <RankBeltDisplay
        belt={belt}
        beltSystemKey={beltSystemKey}
        width={52}
        height={14}
        colorFocus={colorFocus}
      />
      {label}
    </Box>
  );
}

function isBeltPickerOptionDisabled(
  belt,
  memberBelt,
  memberStripe,
  beltSystemKey,
  allowAnyBelt = false
) {
  if (allowAnyBelt) return false;
  if (memberBelt == null || memberBelt === "") return false;
  return !isBeltColorPromotionAllowed(belt, memberBelt, memberStripe, beltSystemKey);
}

/**
 * Promotion belt: Select when empty; after selection, card replaces dropdown until user clicks to change.
 */
export function PromotionBeltPickerField({
  value,
  beltOrder,
  beltSystemKey = "bjj",
  formatBeltName,
  onChange,
  disabled = false,
  isDarkMode,
  placeholder,
  label,
  changeHint,
  fullWidth = true,
  memberBelt,
  memberStripe = 0,
  /** Skip promotion rank rules — admin belt records edit only */
  allowAnyBelt = false,
  /** `compact` = table cells; `form` = dialogs with stacked labels */
  fieldVariant = "compact",
  /** `prominent` = full-width belt bar in menu and selected card */
  visualVariant = "compact",
  /** Compact belt bar emphasizes body color (Promotion by Group table only). */
  beltColorFocus = false,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);
  const isProminent = visualVariant === "prominent";
  const menuItemSx = {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    py: isProminent ? 1.25 : undefined,
    px: isProminent ? 1.5 : undefined,
    minHeight: isProminent ? 64 : undefined,
  };

  const handlePick = (belt) => {
    if (isBeltPickerOptionDisabled(belt, memberBelt, memberStripe, beltSystemKey, allowAnyBelt)) return;
    onChange(belt);
    setMenuAnchor(null);
  };

  const changeMenu = (
    <Menu
      anchorEl={menuAnchor}
      open={menuOpen}
      onClose={() => setMenuAnchor(null)}
      disableScrollLock
      PaperProps={{
        sx: {
          maxHeight: 360,
          minWidth: isProminent ? 280 : undefined,
          zIndex: (theme) => theme.zIndex.modal + 2,
        },
      }}
    >
      {beltOrder.map((belt) => {
        const optionDisabled = isBeltPickerOptionDisabled(
          belt,
          memberBelt,
          memberStripe,
          beltSystemKey,
          allowAnyBelt
        );
        return (
          <MenuItem
            key={belt}
            selected={belt === value}
            disabled={optionDisabled}
            onClick={() => handlePick(belt)}
            sx={menuItemSx}
          >
            {renderBeltMenuItemContent(
              belt,
              beltSystemKey,
              formatBeltName,
              optionDisabled,
              visualVariant,
              beltColorFocus
            )}
          </MenuItem>
        );
      })}
    </Menu>
  );

  const isForm = fieldVariant === "form";
  const selectSx = isForm ? promotionDialogSelectSx(isDarkMode) : promotionGroupSelectSx(isDarkMode);
  const fieldWrapSx = isForm ? { width: "100%", mb: 0 } : { width: fullWidth ? "100%" : undefined };

  if (value) {
    return (
      <Box sx={fieldWrapSx}>
        {isForm ? <StackedFieldLabel isDarkMode={isDarkMode}>{label || placeholder}</StackedFieldLabel> : null}
        <PromotionBeltSelectedCard
          belt={value}
          beltSystemKey={beltSystemKey}
          formatBeltName={formatBeltName}
          isDarkMode={isDarkMode}
          disabled={disabled}
          changeHint={changeHint}
          visualVariant={visualVariant}
          colorFocus={beltColorFocus}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        />
        {!disabled && changeMenu}
      </Box>
    );
  }

  if (isForm) {
    return (
      <Box sx={fieldWrapSx}>
        <StackedFieldLabel isDarkMode={isDarkMode}>{label || placeholder}</StackedFieldLabel>
        <FormControl fullWidth={fullWidth} size="small" disabled={disabled}>
          <Select
            displayEmpty
            value=""
            onChange={(e) => onChange(e.target.value)}
            renderValue={() => (
              <Typography
                component="span"
                sx={{
                  fontSize: "0.875rem",
                  color: getMm(isDarkMode).onSurfaceVariant,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                {placeholder}
              </Typography>
            )}
          MenuProps={{
            disableScrollLock: true,
            PaperProps: {
              sx: {
                maxHeight: 320,
                backgroundColor: getMm(isDarkMode).surfaceLowest,
              },
            },
          }}
            sx={selectSx}
          >
            {beltOrder.map((belt) => {
              const optionDisabled = isBeltPickerOptionDisabled(
                belt,
                memberBelt,
                memberStripe,
                beltSystemKey,
                allowAnyBelt
              );
              return (
                <MenuItem
                  key={belt}
                  value={belt}
                  disabled={optionDisabled}
                  sx={menuItemSx}
                >
                  {renderBeltMenuItemContent(
                    belt,
                    beltSystemKey,
                    formatBeltName,
                    optionDisabled,
                    visualVariant,
                    beltColorFocus
                  )}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>
    );
  }

  return (
    <FormControl fullWidth={fullWidth} size="small" disabled={disabled}>
      {label ? <InputLabel shrink>{label}</InputLabel> : null}
      <Select
        displayEmpty
        value=""
        label={label || undefined}
        onChange={(e) => onChange(e.target.value)}
        renderValue={() => (
          <Typography
            component="span"
            sx={{
              fontSize: "0.8125rem",
              color: getMm(isDarkMode).onSurfaceVariant,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {placeholder}
          </Typography>
        )}
        sx={selectSx}
      >
        {beltOrder.map((belt) => {
          const optionDisabled = isBeltPickerOptionDisabled(
            belt,
            memberBelt,
            memberStripe,
            beltSystemKey,
            allowAnyBelt
          );
          return (
            <MenuItem
              key={belt}
              value={belt}
              disabled={optionDisabled}
              sx={menuItemSx}
            >
              {renderBeltMenuItemContent(
                belt,
                beltSystemKey,
                formatBeltName,
                optionDisabled,
                visualVariant,
                beltColorFocus
              )}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

function renderStripeMenuItemContent(
  stripe,
  belt,
  beltSystemKey,
  formatStripeLabel,
  disabled,
  visualVariant = "compact"
) {
  const label =
    typeof formatStripeLabel === "function" ? formatStripeLabel(stripe) : String(stripe);
  const isProminent = visualVariant === "prominent";
  const beltForVisual = belt || "white";

  if (isProminent) {
    return (
      <Box
        sx={{
          width: "100%",
          py: 0.25,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          opacity: disabled ? 0.45 : 1,
        }}
      >
        <RankBeltDisplay
          belt={beltForVisual}
          beltSystemKey={beltSystemKey}
          stripeCount={stripe}
          width={320}
          height={24}
          fullWidth
          sx={{ width: "100%", mb: 0.75 }}
        />
        <Typography
          component="span"
          sx={{
            display: "block",
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: "inherit",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <PromotionStripeSelectValue
        stripeCount={stripe}
        belt={beltForVisual}
        beltSystemKey={beltSystemKey}
      />
      {label}
    </Box>
  );
}

function hasStripePickerValue(value) {
  return value !== "" && value !== undefined && value !== null;
}

/** Selected stripe rank: card UI. Click opens change menu. */
export function PromotionStripeSelectedCard({
  stripeCount,
  belt = "white",
  beltSystemKey = "bjj",
  formatStripeLabel,
  isDarkMode,
  onClick,
  disabled,
  changeHint,
  visualVariant = "compact",
}) {
  if (!hasStripePickerValue(stripeCount)) return null;
  const label =
    typeof formatStripeLabel === "function"
      ? formatStripeLabel(stripeCount)
      : String(stripeCount);
  const isProminent = visualVariant === "prominent";

  return (
    <Box
      role={disabled ? undefined : "button"}
      tabIndex={disabled ? undefined : 0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={
        disabled
          ? undefined
          : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.(e);
              }
            }
      }
      title={disabled ? undefined : changeHint}
      sx={{
        display: "flex",
        flexDirection: isProminent ? "column" : "row",
        alignItems: isProminent ? "stretch" : "center",
        gap: 1,
        px: isProminent ? 1.25 : 1,
        py: isProminent ? 1.25 : 0.75,
        minHeight: isProminent ? 72 : 36,
        width: "100%",
        boxSizing: "border-box",
        borderRadius: "8px",
        border: "1px solid",
        borderColor: getMm(isDarkMode).outlineVariant,
        bgcolor: getMm(isDarkMode).surfaceLow,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.65 : 1,
        transition: "border-color 0.15s ease, background-color 0.15s ease",
        ...(!disabled && {
          "&:hover": {
            borderColor: isDarkMode ? "#6B7280" : "#D1D5DB",
            bgcolor: isDarkMode ? "#4B5563" : "#F3F4F6",
          },
        }),
      }}
    >
      <RankBeltDisplay
        belt={belt || "white"}
        beltSystemKey={beltSystemKey}
        stripeCount={stripeCount}
        width={isProminent ? 320 : 88}
        height={isProminent ? 24 : 14}
        fullWidth={isProminent}
        sx={isProminent ? { width: "100%" } : { flexShrink: 0 }}
      />
      <Typography
        component="span"
        noWrap={!isProminent}
        sx={{
          flex: isProminent ? undefined : 1,
          minWidth: 0,
          fontSize: "0.8125rem",
          fontWeight: 600,
          lineHeight: 1.2,
          color: getMm(isDarkMode).onSurface,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          textAlign: "left",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/**
 * Promotion stripe: Select when empty; after selection, card replaces dropdown until user clicks to change.
 */
export function PromotionStripePickerField({
  value,
  stripeValues = [0, 1, 2, 3, 4],
  belt = "white",
  beltSystemKey = "bjj",
  formatStripeLabel,
  onChange,
  disabled = false,
  isDarkMode,
  placeholder,
  label,
  changeHint,
  fullWidth = true,
  fieldVariant = "compact",
  visualVariant = "compact",
  /** Optional per-option disable (e.g. promotion rank rules) */
  isOptionDisabled,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);
  const isProminent = visualVariant === "prominent";
  const isForm = fieldVariant === "form";
  const selectSx = isForm ? promotionDialogSelectSx(isDarkMode) : promotionGroupSelectSx(isDarkMode);
  const fieldWrapSx = isForm ? { width: "100%", mb: 0 } : { width: fullWidth ? "100%" : undefined };
  const menuItemSx = {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    py: isProminent ? 1.25 : undefined,
    px: isProminent ? 1.5 : undefined,
    minHeight: isProminent ? 64 : undefined,
  };

  const optionDisabled = (stripe) =>
    disabled || (typeof isOptionDisabled === "function" ? isOptionDisabled(stripe) : false);

  const handlePick = (stripe) => {
    if (optionDisabled(stripe)) return;
    onChange(stripe);
    setMenuAnchor(null);
  };

  const changeMenu = (
    <Menu
      anchorEl={menuAnchor}
      open={menuOpen}
      onClose={() => setMenuAnchor(null)}
      disableScrollLock
      PaperProps={{
        sx: {
          maxHeight: 360,
          minWidth: isProminent ? 280 : undefined,
          zIndex: (theme) => theme.zIndex.modal + 2,
        },
      }}
    >
      {stripeValues.map((stripe) => {
        const stripeOptionDisabled = optionDisabled(stripe);
        return (
          <MenuItem
            key={stripe}
            selected={stripe === value}
            disabled={stripeOptionDisabled}
            onClick={() => handlePick(stripe)}
            sx={menuItemSx}
          >
            {renderStripeMenuItemContent(
              stripe,
              belt,
              beltSystemKey,
              formatStripeLabel,
              stripeOptionDisabled,
              visualVariant
            )}
          </MenuItem>
        );
      })}
    </Menu>
  );

  if (hasStripePickerValue(value)) {
    return (
      <Box sx={fieldWrapSx}>
        {isForm ? <StackedFieldLabel isDarkMode={isDarkMode}>{label || placeholder}</StackedFieldLabel> : null}
        <PromotionStripeSelectedCard
          stripeCount={value}
          belt={belt}
          beltSystemKey={beltSystemKey}
          formatStripeLabel={formatStripeLabel}
          isDarkMode={isDarkMode}
          disabled={disabled}
          changeHint={changeHint}
          visualVariant={visualVariant}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        />
        {!disabled && changeMenu}
      </Box>
    );
  }

  if (isForm) {
    return (
      <Box sx={fieldWrapSx}>
        <StackedFieldLabel isDarkMode={isDarkMode}>{label || placeholder}</StackedFieldLabel>
        <FormControl fullWidth={fullWidth} size="small" disabled={disabled}>
          <Select
            displayEmpty
            value=""
            onChange={(e) => onChange(e.target.value)}
            renderValue={() => (
              <Typography
                component="span"
                sx={{
                  fontSize: "0.875rem",
                  color: getMm(isDarkMode).onSurfaceVariant,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                {placeholder}
              </Typography>
            )}
            MenuProps={{
              disableScrollLock: true,
              PaperProps: {
                sx: {
                  maxHeight: 360,
                  backgroundColor: getMm(isDarkMode).surfaceLowest,
                },
              },
            }}
            sx={selectSx}
          >
            {stripeValues.map((stripe) => {
              const stripeOptionDisabled = optionDisabled(stripe);
              return (
              <MenuItem key={stripe} value={stripe} disabled={stripeOptionDisabled} sx={menuItemSx}>
                {renderStripeMenuItemContent(
                  stripe,
                  belt,
                  beltSystemKey,
                  formatStripeLabel,
                  stripeOptionDisabled,
                  visualVariant
                )}
              </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>
    );
  }

  return (
    <FormControl fullWidth={fullWidth} size="small" disabled={disabled}>
      {label ? <InputLabel shrink>{label}</InputLabel> : null}
      <Select
        displayEmpty
        value=""
        label={label || undefined}
        onChange={(e) => onChange(e.target.value)}
        renderValue={() => (
          <Typography
            component="span"
            sx={{
              fontSize: "0.8125rem",
              color: getMm(isDarkMode).onSurfaceVariant,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {placeholder}
          </Typography>
        )}
        sx={selectSx}
      >
        {stripeValues.map((stripe) => {
          const stripeOptionDisabled = optionDisabled(stripe);
          return (
          <MenuItem key={stripe} value={stripe} disabled={stripeOptionDisabled} sx={menuItemSx}>
            {renderStripeMenuItemContent(
              stripe,
              belt,
              beltSystemKey,
              formatStripeLabel,
              stripeOptionDisabled,
              visualVariant
            )}
          </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

/** @deprecated Use PromotionBeltSelectedCard / PromotionBeltPickerField */
export function PromotionBeltSelectValue({
  belt,
  beltSystemKey = "bjj",
  formatBeltName,
}) {
  if (!belt) return null;
  const label =
    typeof formatBeltName === "function"
      ? formatBeltName(belt)
      : belt;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        maxWidth: "100%",
        overflow: "hidden",
        lineHeight: 1,
      }}
    >
      <RankBeltDisplay
        belt={belt}
        beltSystemKey={beltSystemKey}
        width={44}
        height={10}
        sx={{ flexShrink: 0 }}
      />
      <Typography
        component="span"
        noWrap
        sx={{
          fontSize: "0.8125rem",
          fontWeight: 600,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export function PromotionStripeSelectValue({ stripeCount, belt = "white", beltSystemKey = "bjj" }) {
  if (stripeCount === "" || stripeCount === undefined) return null;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", lineHeight: 1 }}>
      <RankBeltDisplay
        belt={belt}
        beltSystemKey={beltSystemKey}
        stripeCount={stripeCount}
        width={88}
        height={14}
      />
    </Box>
  );
}
